import React, { useState, useEffect, useRef, useMemo } from "react";
import "./styles/home.css";
import { FaGithub, FaLinkedin } from "react-icons/fa";
import { Link } from "react-router-dom";
import { HeroVortex } from "../ui/starfield";

// Roadmap Component
function Roadmap({ techList, positions }) {
  const svgRef = useRef(null);
  const [svgSize, setSvgSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateSize = () => {
      if (svgRef.current) {
        setSvgSize({
          width: svgRef.current.clientWidth,
          height: svgRef.current.clientHeight,
        });
      }
    };
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const paths = [];
  for (let i = 0; i < techList.length - 1; i++) {
    const start = positions[i];
    const end = positions[i + 1];

    const x1 = (parseFloat(start.left) / 100) * svgSize.width;
    const y1 = (parseFloat(start.top) / 100) * svgSize.height;
    const x2 = (parseFloat(end.left) / 100) * svgSize.width;
    const y2 = (parseFloat(end.top) / 100) * svgSize.height;

    // Slight curve with midpoint
    const dx = (x2 - x1) / 2; // control point for proper curve
    paths.push(
      <path
        key={i}
        d={`M${x1} ${y1} C${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`}
        stroke="#0ff"
        strokeWidth="2"
        fill="transparent"
        strokeDasharray="5,5"
        className="roadmap-line"
      />
    );
  }

  return (
    <div className="roadmap-container">
      <svg ref={svgRef} className="roadmap-svg">
        {paths}
      </svg>
      {techList.map((tech, i) => (
        <div
          className="roadmap-node"
          key={tech.name}
          style={{ left: positions[i].left, top: positions[i].top }}
        >
          <div className="tech-icon">
            <img src={tech.src} alt={tech.name} />
          </div>
          <p>{tech.name}</p>
        </div>
      ))}
    </div>
  );
}

// Home Component
function Home() {
  const fullText = "Engineer. Builder. Techpreneur.";
  const [displayedText, setDisplayedText] = useState("");

  // Typing animation
  useEffect(() => {
    let index = 0;
    const interval = setInterval(() => {
      setDisplayedText(fullText.slice(0, ++index));
      if (index === fullText.length) clearInterval(interval);
    }, 100);
    return () => clearInterval(interval);
  }, [fullText]);


  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const size = 320;
    canvas.width = size;
    canvas.height = size;

    const cx = size / 2;
    const cy = size / 2;
    const radius = 120;

    const POINTS = 120;
    const MAX_DIST = 42;

    const points = [];

    // Generate points on sphere surface
    for (let i = 0; i < POINTS; i++) {
      const u = Math.random();
      const v = Math.random();

      const theta = 2 * Math.PI * u;
      const phi = Math.acos(2 * v - 1);

      points.push({
        x: radius * Math.sin(phi) * Math.cos(theta),
        y: radius * Math.cos(phi),
        z: radius * Math.sin(phi) * Math.sin(theta)
      });
    }

    let angle = 0;

    function project(p) {
      const depth = 420;
      const scale = depth / (depth + p.z);
      return {
        x: cx + p.x * scale,
        y: cy + p.y * scale,
        scale
      };
    }

    function animate() {
      ctx.clearRect(0, 0, size, size);
      angle += 0.002;

      const cos = Math.cos(angle);
      const sin = Math.sin(angle);

      const rotated = points.map(p => ({
        x: p.x * cos - p.z * sin,
        y: p.y,
        z: p.x * sin + p.z * cos
      }));

      const projected = rotated.map(p => project(p));

      /* ----- DRAW CONNECTIONS (FIXED) ----- */
      ctx.lineWidth = 1;
      ctx.shadowBlur = 8;
      ctx.shadowColor = "rgba(0, 255, 255, 0.6)";

      for (let i = 0; i < rotated.length; i++) {
        for (let j = i + 1; j < rotated.length; j++) {
          const dx = rotated[i].x - rotated[j].x;
          const dy = rotated[i].y - rotated[j].y;
          const dz = rotated[i].z - rotated[j].z;
          const dist = Math.sqrt(dx*dx + dy*dy + dz*dz);

          if (dist < MAX_DIST) {
            const alpha = 1 - dist / MAX_DIST;
            ctx.strokeStyle = `rgba(0, 255, 255, ${alpha * 0.6})`;

            ctx.beginPath();
            ctx.moveTo(projected[i].x, projected[i].y);
            ctx.lineTo(projected[j].x, projected[j].y);
            ctx.stroke();
          }
        }
      }

      /* ----- DRAW POINTS ----- */
      ctx.shadowBlur = 12;
      ctx.shadowColor = "rgba(0, 255, 255, 0.9)";

      projected.forEach(p => {
        ctx.fillStyle = "rgba(0,255,255,0.9)";
        ctx.beginPath();
        ctx.arc(p.x, p.y, 2.2 * p.scale, 0, Math.PI * 2);
        ctx.fill();
      });

      requestAnimationFrame(animate);
    }

    animate();
  }, []);


  // Tech Data
  const webTech = [
    { src: "/logos/react.png", name: "React" },
    { src: "/logos/nodejs.png", name: "Node.js" },
    { src: "/logos/js.png", name: "JavaScript" },
    { src: "/logos/express.png", name: "Express.js" },
    { src: "/logos/mongodb.png", name: "MongoDB" },
    { src: "/logos/html.png", name: "HTML" },
    { src: "/logos/css.png", name: "CSS" },
  ];

  const embeddedTech = [
    { src: "/logos/c.png", name: "C" },
    { src: "/logos/c++.png", name: "C++" },
    { src: "/logos/arm.png", name: "ARM Cortex" },
    { src: "/logos/esp32.png", name: "ESP32" },
    { src: "/logos/arduino.png", name: "Arduino" },
    { src: "/logos/keil.png", name: "Keil μVision" },
  ];

  const webPositions = [
    { left: "10%", top: "60%" },
    { left: "25%", top: "30%" },
    { left: "40%", top: "50%" },
    { left: "55%", top: "20%" },
    { left: "70%", top: "40%" },
    { left: "85%", top: "25%" },
    { left: "95%", top: "50%" },
  ];

  const embeddedPositions = [
    { left: "5%", top: "50%" },
    { left: "20%", top: "20%" },
    { left: "40%", top: "40%" },
    { left: "60%", top: "15%" },
    { left: "75%", top: "45%" },
    { left: "90%", top: "25%" },
  ];

  const images = useMemo(() => [
    "/catalogue.png",
    "/responsive-img.png",
    "/landing-page.png",
    "/responsive.png",
    "/dashboard.png",
  ], []);

  const total = images.length;
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 4000);

    return () => clearInterval(interval);
  }, [total]); // ESLint is now happy

  const sliderRef = useRef(null);

const scrollSlide = (direction) => {
  if (!sliderRef.current) return;

  const slideWidth = sliderRef.current.clientWidth;
  sliderRef.current.scrollBy({
    left: direction * slideWidth,
    behavior: "smooth",
  });
};

  return (
    <>
      {/* Hero Section */}
      <section className="landing-hero">
        <div className="hero-shapes">
          <span></span>
        <div className="globe-wrapper">
          <canvas ref={canvasRef} />
        </div>
          <span></span>
          <span></span>
        </div>

        <div className="hero-graphic">
          <img src="/greek.png" alt="Greek figure" />
        </div>

        <div className="hero-accent-shape">
          <div className="corner top-left"></div>
          <div className="corner bottom-left"></div>
        </div>

        <div className="hero-content">
          <h1 className="hero-title">JOEL REJI GEORGE</h1>
          <h2 className="hero-subtitle">{displayedText}</h2>
          <p className="hero-tagline">
            Building futuristic web experiences, one line of code at a time.
          </p>
          <Link to="/projects" className="hero-button">
            View Projects
          </Link>
        </div>
      </section>

      <section className="w-full mx-auto px-6 py-24 bg-[var(--bg-surface)]">
        <div className="grid lg:grid-cols-2 gap-16 items-center">

          {/* LEFT CONTENT */}
          <div>
            <span className="
              inline-block mb-6 px-4 py-1 text-sm rounded-full
              bg-[var(--border-soft)]
              text-[var(--text-secondary)]
              border border-[var(--border-soft)]
            ">
              Available for freelance work
            </span>

            <h1 className="text-5xl font-bold leading-tight text-[var(--text-secondary)] mb-4">
              Hi, I'm <span className="text-indigo-600">Joel</span>
            </h1>

            <h2 className="text-2xl font-semibold text-[var(--text-secondary)] mb-4">
              Software Developer.
            </h2>

            <p className="text-[var(--text-secondary)] max-w-lg mb-8">
              I create beautiful, functional, and user-centered digital experiences.
              With 2+ years of experience in web development, I bring ideas to life
              through clean code and thoughtful design.
            </p>

            {/* Location */}
            <div className="flex items-center text-sm text-[var(--text-secondary)] mb-6">
              <span> Based in India</span>
              <span className="ml-6 text-indigo-600 cursor-pointer">Available Now</span>
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <button className="px-6 py-3 bg-[var(--bg-primary)] text-[var(--text-primary)] rounded-full text-sm hover:bg-[var(--border-soft)] transition">
                Hire Me
              </button>
              <button className="px-6 py-3 border border-gray-300 rounded-full text-sm text-[var(--text-primary)] hover:bg-[var(--border-soft)] transition">
                <a
                  href="https://drive.google.com/file/d/1El5cm66w5oqSrMD1c-kAtz0Lr4BejiiF/view?usp=sharing"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download CV
                </a>              
              </button>
            </div>

            {/* Socials */}
            <div className="flex items-center gap-5 text-[var(--text-secondary)] text-lg">
              <FaGithub className="hover:text-[var(--text-secondary)] cursor-pointer" />
              <FaLinkedin className="hover:text-black cursor-pointer" />
            </div>
          </div>

          {/* RIGHT IMAGE */}
          <div className="flex justify-center lg:justify-center mt-6 mb-6 md:mt-12 md:mb-12 lg:mt-0 lg:mb-0">
            <div className="relative w-full h-[400px] md:h-[600px] lg:h-[700px] overflow-hidden">
              {images.map((img, i) => (
                <img
                  key={i}
                  src={img}
                  alt="slider"
                  className={`
                    absolute inset-0 w-full h-full
                    object-contain
                    transition-opacity duration-1000 ease-in-out
                    ${i === index ? "opacity-100" : "opacity-0"}
                  `}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="tech-section relative overflow-hidden">
        {/* Background layer */}
        <HeroVortex />
        {/* Foreground content */}
        <div className="tech-showcase relative z-10">
          <h2 className="section-title">Tech I Work With</h2>

          <div className="tech-slider">
            <button className="nav-btn left" onClick={() => scrollSlide(-1)}>
              ‹
            </button>

            <div className="slides" ref={sliderRef}>
              {/* Slide 1 – Web */}
              <div className="slide">
                <div className="tech-category web">
                  <h3 className="tech-header">Web Development</h3>
                  <div className="tech-wrapper">
                    <div className="tech-textbox">
                      <p>
                        I specialize in building dynamic, responsive, and scalable web
                        applications. From frontend frameworks like React to backend
                        solutions with Node.js and Express, I create modern web experiences
                        that perform and scale.
                      </p>
                    </div>
                    <Roadmap techList={webTech} positions={webPositions} />
                  </div>
                </div>
              </div>

              {/* Slide 2 – Embedded */}
              <div className="slide">
                <div className="tech-category embedded">
                  <h3 className="tech-header">Embedded Development</h3>
                  <div className="tech-wrapper reverse">
                    <Roadmap techList={embeddedTech} positions={embeddedPositions} />
                    <div className="tech-textbox">
                      <p>
                        My embedded systems journey involves working with microcontrollers,
                        real-time programming, and hardware integration. From C/C++ to ESP32
                        and Arduino ecosystems, I bring code closer to hardware.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <button className="nav-btn right" onClick={() => scrollSlide(1)}>
              ›
            </button>
          </div>
        </div>
      </section>



      <section className="skills-section">
      <h2 className="section-title">Logged Hours</h2>

      <div className="skills-wrapper">
        {/* Skill Item */}
        <div className="skill-item">
          <p className="skill-name">React</p>
          <div className="skill-bar">
            <div className="skill-fill" style={{ "--bar-width": "90%" }}></div>
          </div>
          <p>608</p>
        </div>

        <div className="skill-item">
          <p className="skill-name">Node.js</p>
            <div className="skill-bar">
              <div className="skill-fill" style={{ "--bar-width": "85%" }}></div>
            </div>
          <p>596</p>
        </div>

        <div className="skill-item">
          <p className="skill-name">Embedded C/C++</p>
          <div className="skill-bar">
            <div className="skill-fill" style={{ "--bar-width": "45%" }}></div>
          </div>
          <p>186</p>
        </div>

        <div className="skill-item">
          <p className="skill-name">MongoDB</p>
          <div className="skill-bar">
              <div className="skill-fill" style={{ "--bar-width": "55%" }}></div>
          </div>
          <p>450</p>
        </div>
      </div>
      </section>

    </>
  );
}

export default Home;